import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChatView } from './ChatView';
import { ChatSettingsDialog } from './ChatSettingsDialog';
import { useAuth } from '@/hooks/use-auth';
import {
  useProjectChatAccess,
  useProjectChatMessages,
  useProjectChatMembers,
  useSendProjectChatMessage,
} from '@/hooks/use-chats';
import { useSocket } from '@/hooks/use-socket';
import type { ProjectChat, ProjectChatMessage } from '@/types';

interface ProjectChatViewProps {
  chat: ProjectChat;
  projectId: string;
}

export function ProjectChatView({ chat, projectId }: ProjectChatViewProps) {
  const [showSettings, setShowSettings] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();

  const { data: access, isLoading: accessLoading } = useProjectChatAccess(chat.id);
  const { data: messages = [], isLoading: messagesLoading } = useProjectChatMessages(
    chat.id,
    !!access?.canRead
  );
  const { data: members = [], isLoading: membersLoading } = useProjectChatMembers(
    chat.id,
    !!access?.canRead
  );
  const sendMessage = useSendProjectChatMessage(chat.id);

  // Subscribe to chat socket events
  useEffect(() => {
    if (!socket || !isConnected || !access?.canRead) return;

    socket.emit('subscribe:project-chat', { chatId: chat.id });

    const handleNewMessage = (message: ProjectChatMessage) => {
      let appendedNew = false;
      queryClient.setQueryData<ProjectChatMessage[]>(
        ['project-chat-messages', chat.id],
        (old) => {
          // Avoid duplicates
          if (old?.some((m) => m.id === message.id)) return old;
          appendedNew = true;
          return [...(old || []), message];
        }
      );
      if (appendedNew) {
        void queryClient.invalidateQueries({ queryKey: ['all-chats'] });
      }
    };

    socket.on('project-chat:message', handleNewMessage);

    return () => {
      socket.emit('unsubscribe:project-chat', { chatId: chat.id });
      socket.off('project-chat:message', handleNewMessage);
    };
  }, [socket, isConnected, chat.id, access?.canRead, queryClient]);

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!access?.canRead) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">You don't have access to this chat</p>
      </div>
    );
  }

  const title = chat.type === 'internal' ? 'Internal Chat' : 'Client Chat';

  return (
    <>
      <ChatView
        messages={messages}
        access={access}
        isLoading={messagesLoading}
        isSending={sendMessage.isPending}
        onSendMessage={(content) => sendMessage.mutate(content)}
        onOpenSettings={() => setShowSettings(true)}
        title={title}
        emptyMessage={`No messages in ${title.toLowerCase()} yet. Start the conversation!`}
        currentUserId={user?.id || ''}
        members={members}
        membersLoading={membersLoading}
      />

      <ChatSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        chatId={chat.id}
        chatType="project"
        projectId={projectId}
      />
    </>
  );
}
